import {
	Box,
	Button,
	Card,
	MenuItem,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from "@mui/material";
import Chip from "@mui/material/Chip";
import { FC, useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSearchParam } from "react-use";
import { ESRThunkDispatch } from "../actions/form";
import backgroundSvg from "../assets/background.svg";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { request } from "../request";
import { ErrorReportModal } from "./ErrorReportModal";
import { useSnackbar } from "notistack";
import {
	isOrganizationChoiceValid,
	OrganizationChoice,
	OrganizationPicker,
} from "./Organizations/OrganizationPicker";
import { getInvitation } from "../api/organizations";

// Signup groups whose users must belong to an Organization (see clowder/users.py)
const ORGANIZATION_GROUPS = ["Local Public Agency", "Consultant"];

export type selectOrg = {
		[key: string]: {
			[key: string]: {
				[key: string]: string[];
			};
		};
};

export const displayOrg: selectOrg = {
		"Illinois Department of Transportation (IDOT)": {
			"IDOT BDE": {
				"CO-BDE": ["CO-BDE (no summary, no notifications)"],
				RSU: [
					"RSU Supervisor (daily/weekly summary)",
					"RSU Phase I (notifications)", // RSU1
					"RSU Phase II (notifications)",
					"RSU District 1 Consultant (notifications)",
					"RSU CDB/CI (notifications)",
				],
				NRU: [
					"NRU Supervisor (daily/weekly summary)",
					"NRU Staff (notifications)", // NRUDX
					"NRU Aeronautics (notifications)",
				],
				CRU: [
					"CRU Supervisor (daily/weekly summary)",
					"CRU Archaeology (notifications)",
					"CRU Architecture (notifications)",
					"CRU Historic Architecture Consultant (notifications)",
				],
			},
			"Local Roads": {
				"N/A": [
					"Local Roads District Staff (notifications)",
					"Local Roads District Supervisor (notifications)",
					"Local Roads Central Office (notifications)",
				],
			},
			"District Environmental Coordinator": { "N/A": [] },
			Aeronautics: {
				"N/A": ["Aeronautics (no notifications)"],
			},
			Railroads: { "N/A": [] },
			"CDB/CI": { "N/A": [] },
		},
		"Non-IDOT/Consultant": {
			"Local Public Agency": { "N/A": [] },
			Consultant: { "N/A": [] },
		},
		"Northern Illinois University": {
			"N/A": {
				"D1,2,3-NIU": [
					"NIU Supervisor (daily/weekly summary)",
					"NIU-Wetlands (notifications)",
					"NIU-Biological Surveys (notifications)",
				],
			},
		},
		"University of Illinois": {
			"N/A": {
				"PRI (all surveys, no notifications)": [],
				INHS: [
					"UIUC Supervisor (daily/weekly summary)",
					"UIUC-Wetlands (notifications)",
					"UIUC-Biological Surveys (notifications)",
				],
				ISGS: [
					"ISGS Wetlands (notifications)",
					"ISGS PESA (daily/weekly summary)",
					"ISGS PESA Read-Only",
				],
				ISWS: ["ISWS (notifications)"],
				ISAS: ["ISAS (notifications)"],
			},
		},
		"Southern Illinois University": {
			"N/A": {
				"D7,8,9-SIUC": [
					"SIUC Supervisor (daily/weekly summary)",
					"SIUC-Wetlands (notifications)",
					"SIUC-Biological Surveys (notifications)",
				],
			},
		},
};

export const Login: FC<{ initialMode?: "signin" | "signup" }> = ({
	initialMode = "signin",
}) => {
	useDocumentTitle("IDOT – TREC Login");
	const dispatch = useDispatch<ESRThunkDispatch>();
	const navigate = useNavigate();
	const landingUrlEncoded = useSearchParam("landing") ?? "/";
	const landingUrl = decodeURIComponent(landingUrlEncoded);
	const [user] = useCurrentUser();
	const hostname = window.location.hostname;
	const isDevMode = hostname.includes("-dev.") || hostname === "localhost";
	const latestDeployment = process.env.REACT_APP_LATEST_DEPLOYMENT;
	const [openErrorModal, setOpenErrorModal] = useState(false);

	const setErrorMsg = useCallback((message: string) => {
		dispatch({ type: "SET_ERROR", error: { message } });
	}, []);

	const county_list: { [key: string]: string[] } = {
		"District 1": ["Cook", "DuPage", "Kane", "Lake", "McHenry", "Will"],
		"District 2": [
			"Boone",
			"Carroll",
			"Henry",
			"JoDaviess",
			"Lee",
			"Ogle",
			"Rock Island",
			"Stephenson",
			"Whiteside",
			"Winnebago",
		],
		"District 3": [
			"Bureau",
			"Dekalb",
			"Ford",
			"Grundy",
			"Iroquois",
			"Kankakee",
			"Kendall",
			"LaSalle",
			"Livingston",
		],
		"District 4": [
			"Fulton",
			"Henderson",
			"Knox",
			"McDonough",
			"Marshall",
			"Mercer",
			"Peoria",
			"Putnam",
			"Stark",
			"Tazewell",
			"Warren",
			"Woodford",
		],
		"District 5": [
			"Champaign",
			"DeWitt",
			"Douglas",
			"Edgar",
			"McLean",
			"Piatt",
			"Vermilion",
		],
		"District 6": [
			"Adams",
			"Brown",
			"Cass",
			"Christian",
			"Hancock",
			"Logan",
			"Macoupin",
			"Mason",
			"Menard",
			"Montgomery",
			"Morgan",
			"Pike",
			"Sangamon",
			"Schuyler",
			"Scott",
		],
		"District 7": [
			"Clark",
			"Clay",
			"Coles",
			"Crawford",
			"Cumberland",
			"Edwards",
			"Effingham",
			"Fayette",
			"Jasper",
			"Lawrence",
			"Macon",
			"Moultrie",
			"Richland",
			"Shelby",
			"Wabash",
			"Wayne.",
		],
		"District 8": [
			"Bond",
			"Calhoun",
			"Clinton",
			"Greene",
			"Jersey",
			"Madison",
			"Marion",
			"Monroe",
			"Randolph",
			"St. Clair",
			"Washington",
		],
		"District 9": [
			"Alexander",
			"Franklin",
			"Gallatin",
			"Hamilton",
			"Hardin",
			"Jackson",
			"Jefferson",
			"Johnson",
			"Massac",
			"Perry",
			"Pope",
			"Pulaski",
			"Saline",
			"Union",
			"White",
			"Williamson",
		],
	};
	const council_list = [
		"01 Chicago",
		"02 North Shore",
		"03 Northwest",
		"04 North Central",
		"05 Central",
		"06 Southwest",
		"07 South",
		"CCDOTH",
	];
	const role_list = ["State", "Local", "CDB/CI", "Aeronautics", "Railroads"];
	let allDistricts: string[] = [];
	let allCounties: string[] = []; // district by county
	let reverseCountyList: Record<string, string> = {};
	Object.entries(county_list).map((entry) => {
		allDistricts.push(entry[0]);
		entry[1].map((c) => {
			allCounties.push(c);
			reverseCountyList[c] = entry[0];
		});
	});
	allDistricts = allDistricts.sort();
	allCounties = allCounties.sort();

	let cdxReplaceMap = {
		// Nice label -> value
		"CRU Architecture (notifications)":
			"CRU Architecture District X Role X (notifications)",
		"RSU Supervisor (daily/weekly summary)": "RSU (daily/weekly summary)",
		"SIUC Supervisor (daily/weekly summary)":
			"D7,8,9-SIUC (daily/weekly summary)",
		"NIU Supervisor (daily/weekly summary)":
			"D1,2,3-NIU (daily/weekly summary)",
		"UIUC Supervisor (daily/weekly summary)":
			"D4,5,6-UIUC (daily/weekly summary)",
		"RSU Phase I (notifications)": "RSU Phase I District X (notifications)",
		"RSU Phase II (notifications)": "RSU Phase II District X (notifications)",
		"RSU CDB/CI (notifications)": "RSU CDB/CI District X (notifications)",
		"NRU Supervisor (daily/weekly summary)": "NRU (daily/weekly summary)",
		"NRU Staff (notifications)": "NRU District X (notifications)",
		"CRU Supervisor (daily/weekly summary)": "CRU (daily/weekly summary)",
		"Local Roads District Supervisor (notifications)":
			"Local Roads District X (notifications)",
		"Local Roads Central Office (notifications)":
			"Local Roads Central Office District X (notifications)",
		"Local Roads District Staff (notifications)":
			"Local Roads District X County X (notifications)",
		"District Environmental Coordinator":
			"District X Environmental Coordinator",
	};
	// Add the inverse for each pair also (value -> nice label)
	Object.entries(cdxReplaceMap).map((entry) => {
		cdxReplaceMap[entry[1]] = entry[0];
	});

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [firstname, setFirstname] = useState("");
	const [lastname, setLastname] = useState("");
	const [phone, setPhone] = useState("");
	const [title, setTitle] = useState("");

	const [selectedOrg, setSelectedOrg] = useState<string>("");
	const [selectedGroup, setSelectedGroup] = useState<string>("");
	const [selectedUnit, setSelectedUnit] = useState<string>("");
	const [selectedRole, setSelectedRole] = useState<string>("");
	const [selectedDistrictRole, setSelectedDistrictRoles] = useState<string>("");
	const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
	const [selectedCounties, setSelectedCounties] = useState<string[]>([]);
	const [selectedCouncils, setSelectedCouncils] = useState<string[]>([]);
	const [cleanGroups, setCleanGroups] = useState<string>("[]");
	const [organization, setOrganization] = useState<OrganizationChoice>(null);
	// Invitation link (?invite=<token>) from an organization invite email
	const inviteToken = useSearchParam("invite");
	const [invitedOrgName, setInvitedOrgName] = useState("");
	useEffect(() => {
		if (!inviteToken) return;
		getInvitation(inviteToken)
			.then(({ email, organization }) => {
				setEmail(email);
				setOrganization({ kind: "existing", id: organization.id });
				setInvitedOrgName(organization.name);
			})
			.catch((e) => console.error(e));
	}, [inviteToken]);
	const needsOrganization = ORGANIZATION_GROUPS.includes(selectedGroup);
	const [valid, setValid] = useState(false);
	const [mode, setMode] = useState<"signin" | "signup">(initialMode);
	const keycloakAuthUrl =
		`${process.env.REACT_APP_KEYCLOAK_URL}/realms/clowder/protocol/openid-connect/auth?` +
		new URLSearchParams({
			client_id: String(process.env.REACT_APP_KEYCLOAK_CLIENT_ID),
			response_type: "code",
			redirect_uri: String(process.env.REACT_APP_KEYCLOAK_REDIRECT_URI),
			scope: "openid email profile",
		});

	useEffect(() => {
		if (user) {
			navigate(landingUrl, { replace: true });
		}
	}, [user]);

	useEffect(() => {
		if (!user && mode === "signin") {
			window.location.replace(keycloakAuthUrl);
		}
	}, [user, mode, keycloakAuthUrl]);

	useEffect(() => {
		// Determine what the current selections are for group
		let cleanRole =
			selectedRole.length > 0 && selectedRole !== "N/A"
				? selectedRole.split(" (")[0]
				: selectedUnit.length > 0 &&
					  selectedUnit !== "N/A" &&
					  selectedRole === "N/A"
					? selectedUnit.split(" (")[0]
					: selectedGroup.length > 0 &&
						  selectedGroup !== "N/A" &&
						  selectedUnit === "N/A" &&
						  selectedRole === "N/A"
						? selectedGroup.split(" (")[0]
						: "";

		if (selectedOrg !== "Non-IDOT/Consultant") {
			if (phone.length > 0) setPhone("");
			if (title.length > 0) setTitle("");
		}

		// Check all required fields are filled to enable Sign Up
		let allOK =
			email.length > 0 &&
			password.length > 0 &&
			firstname.length > 0 &&
			lastname.length > 0 &&
			cleanRole.length > 0 &&
			(selectedOrg !== "Non-IDOT/Consultant" ||
				(phone.length > 0 && title.length > 0)) &&
			(!needsOrganization || isOrganizationChoiceValid(organization));
		if (/District X/g.test(cleanRole) && selectedDistricts.length === 0)
			allOK = false;
		if (/County X/g.test(cleanRole) && selectedCounties.length === 0)
			allOK = false;
		if (/Role X/g.test(cleanRole) && selectedDistrictRole.length === 0)
			allOK = false;
		if (
			selectedCounties.some((v) => v === "Cook") &&
			selectedCouncils.length === 0
		)
			allOK = false;
		setValid(allOK);

		if (cleanRole.length > 0) {
			// Create clean group names
			let groupNames: string[] = [];
			if (cleanRole.includes("District X")) {
				selectedDistricts.map((dist) => {
					if (cleanRole.includes("Role X")) {
						if (selectedDistrictRole.length > 0) {
							groupNames.push(
								cleanRole
									.replace("District X", dist)
									.replace("Role X", selectedDistrictRole),
							);
						}
					} else if (cleanRole.includes("County X")) {
						selectedCounties.map((cty) => {
							if (dist === "District 1" && cty === "Cook") {
								selectedCouncils.map((ccl) => {
									groupNames.push(
										cleanRole
											.replace("District X", dist)
											.replace("County X", cty) +
											" " +
											ccl,
									);
								});
							} else if (county_list[dist].includes(cty)) {
								groupNames.push(
									cleanRole
										.replace("District X", dist)
										.replace("County X", cty),
								);
							}
						});
					} else {
						groupNames.push(cleanRole.replace("District X", dist));
					}
				});
			} else {
				groupNames.push(cleanRole);
			}
			setCleanGroups(JSON.stringify(groupNames));
		} else {
			setCleanGroups("[]");
		}
	}, [
		email,
		password,
		firstname,
		lastname,
		phone,
		title,
		selectedOrg,
		selectedGroup,
		selectedUnit,
		selectedRole,
		selectedCounties,
		selectedDistricts,
		selectedCouncils,
		selectedDistrictRole,
		organization,
	]);

	useEffect(() => {
		// If district changes and county is invalid, remove county setting
		let allowedCounties: string[] = [];
		let newSelectedCounties: string[] = [];
		selectedDistricts.map((entry: string) => {
			county_list[entry].map((cty: string) => allowedCounties.push(cty));
		});

		selectedCounties.map((cty) => {
			if (allowedCounties.includes(cty)) {
				newSelectedCounties.push(cty);
			}
		});
		setSelectedCounties(newSelectedCounties);
	}, [selectedDistricts]);

	useEffect(() => {
		// If county is selected, make sure its district is also selected
		let newSelectedDistricts: string[] = selectedDistricts;
		selectedCounties.map((cty) => {
			allDistricts.map((entry) => {
				if (
					county_list[entry].includes(cty) &&
					!newSelectedDistricts.includes(entry)
				)
					newSelectedDistricts.push(entry);
			});
		});
		setSelectedDistricts(newSelectedDistricts);
	}, [selectedCounties]);

	const { enqueueSnackbar, closeSnackbar } = useSnackbar();

	const register = useCallback(() => {
		const email_fmt = email.toLowerCase();
		const email_frequency =
			selectedUnit.includes("(daily/weekly summary)") ||
			selectedRole.includes("(daily/weekly summary)")
				? "Weekly"
				: "None";
		// TODO: notifications frequency?
		// const notifications_frequency = ""
		request
			.post(`${process.env.REACT_APP_API_SERVER}/clowder/create_user`, {
				first_name: firstname,
				last_name: lastname,
				email: email_fmt,
				password: password,
				groups: cleanGroups,
				phone: phone,
				title: title,
				email_frequency: email_frequency,
				...(needsOrganization && organization?.kind === "existing"
					? { organization_id: organization.id }
					: {}),
				...(needsOrganization && organization?.kind === "new"
					? { new_organization: organization.info }
					: {}),
			}, {
				// 400s carry a user-facing "error" message, which the request interceptor displays
				validateStatus: (status) => status < 500,
			})
			.then((res) => {
				if (res.status === 200) {
					enqueueSnackbar({
						variant: "success",
						message:
							"Your account has been created and is pending review for activation.",
					});
					// User is created inactive. Add to groups for admins to approve.
					// dispatch(addUserToGroups(email_fmt, cleanGroups))
					//login();
					// switchToSignin();
					// setErrorMsg(
					// 	"Your account has been created and is pending review for activation.",
					// );
				} else if (!res.data?.error) {
					setErrorMsg("Something went wrong... Try again.");
				}
			})
			.catch((e) => {
				console.error(e);
				setErrorMsg("Something went wrong... Try again.");
			});
	}, [
		email,
		password,
		firstname,
		lastname,
		cleanGroups,
		selectedUnit,
		selectedRole,
		phone,
		title,
		needsOrganization,
		organization,
	]);

	useEffect(() => {
		const error = new URLSearchParams(window.location.search).get("error");
		if (error) {
			const url = new URL(window.location.href);
			url.search = "";
			window.history.replaceState(null, "", url);
			enqueueSnackbar({
				key: "login-error",
				variant: "warning",
				message: error,
				persist: true,
				action: (key) => (
					<Button
						sx={{ color: "white" }}
						onClick={() => {
							closeSnackbar(key);
						}}
					>
						Done
					</Button>
				),
			});
		}
	}, []);

	return (
		<>
			<Box
				sx={{
					height: "100vh",
					width: "100vw",
					display: "flex",
					direction: "row",
					alignItems: "center",
					justifyContent: "center",
					gap: 10,
					background: `url(${backgroundSvg})`,
					backgroundSize: "cover",
				}}
			>
				<Card
					component="form"
					onSubmit={(e) => {
						e.preventDefault();
						if (mode === "signup") {
							register();
						}
					}}
					sx={{
						width: "600px",
						maxHeight: "768px",
						boxSizing: "border-box",
						position: "relative",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Box
						sx={{
							px: "80px",
							py: "120px",
						}}
					>
						{mode === "signin" && (
							<Typography sx={{ mt: "24px" }}>
								Redirecting to sign in…
							</Typography>
						)}
						{mode === "signup" && (
							<>
								<Typography component="h1" fontSize={32} fontWeight={300}>
									IDOT TREC — Sign Up
								</Typography>
								{isDevMode && latestDeployment && (
									<Typography
										sx={{ mt: "8px" }}
										color="warning.main"
										fontSize={14}
										fontWeight={600}
									>
										Latest Deployment: {latestDeployment}
									</Typography>
								)}
								{invitedOrgName && (
									<Typography sx={{ mt: "16px" }} color="primary" fontSize={14}>
										You've been invited to join {invitedOrgName}. Sign up with
										this email, then choose Non-IDOT/Consultant and your role;
										your organization is already selected.
									</Typography>
								)}
								<TextField
									sx={{ mt: "24px" }}
									fullWidth
									type="email"
									label="email"
									placeholder="email"
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
								<TextField
									sx={{ mt: "24px" }}
									fullWidth
									type="password"
									label="password"
									placeholder="password"
									value={password}
									onChange={(e) => setPassword(e.target.value)}
								/>
								<Box>
									<Box alignItems="center" display="flex" gap={4}>
										<TextField
											sx={{ mt: "24px" }}
											fullWidth
											type="firstname"
											label="firstname"
											placeholder="firstname"
											value={firstname}
											onChange={(e) => setFirstname(e.target.value)}
										/>
										<TextField
											sx={{ mt: "24px" }}
											fullWidth
											type="lastname"
											label="lastname"
											placeholder="lastname"
											value={lastname}
											onChange={(e) => setLastname(e.target.value)}
										/>
									</Box>
									{selectedOrg === "Non-IDOT/Consultant" ? (
										<Box alignItems="center" display="flex" gap={4}>
											<TextField
												sx={{ mt: "24px" }}
												fullWidth
												type="phone"
												label="phone"
												placeholder="phone"
												value={phone}
												onChange={(e) => setPhone(e.target.value)}
											/>
											<TextField
												sx={{ mt: "24px" }}
												fullWidth
												type="title"
												label="title"
												placeholder="title"
												value={title}
												onChange={(e) => setTitle(e.target.value)}
											/>
										</Box>
									) : (
										<></>
									)}
								</Box>
							</>
						)}
						{mode === "signup" && (
							<>
								<Button
									sx={{ mt: "48px" }}
									fullWidth
									disabled={!valid}
									variant="contained"
									size="large"
									type="submit"
								>
									Sign Up
								</Button>
								<Button sx={{ mt: "16px" }} href="/login">
									Have a TREC account already? Sign in instead
								</Button>
								<Button
									sx={{ mt: "16px" }}
									onClick={() => setOpenErrorModal(true)}
								>
									Report Login Error
								</Button>
							</>
						)}
					</Box>
				</Card>

				<Box>
					<Card
						sx={{
							width: "600px",
							maxHeight: "768px",
							boxSizing: "border-box",
							position: "relative",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<Box
							sx={{
								flex: "auto",
								overflowY: "scroll",
								px: "80px",
								py: "120px",
							}}
						>
							<Box>
								<Typography marginTop={4} fontSize={16} fontWeight={600}>
									Use the form below to select your role in the system. At least
									one is required.
								</Typography>
								<Select
									sx={{ mt: "24px" }}
									fullWidth
									value={selectedOrg}
									onChange={(e: SelectChangeEvent<typeof selectedOrg>) => {
										const {
											target: { value },
										} = e;
										setSelectedOrg(value);
										const possibleGroups = Object.keys(displayOrg[value]);
										if (
											possibleGroups.length === 1 &&
											possibleGroups[0] === "N/A"
										) {
											setSelectedGroup("N/A");
											setSelectedUnit("");
											setSelectedRole("");
										} else if (possibleGroups.length === 1) {
											setSelectedGroup(possibleGroups[0]);
											setSelectedUnit("");
											setSelectedRole("");
										} else {
											setSelectedGroup("");
											setSelectedUnit("");
											setSelectedRole("");
										}
									}}
									displayEmpty
									renderValue={(value: any) => {
										if (value.length === 0) {
											return (
												<Typography color="gray">
													Select organization
												</Typography>
											);
										} else {
											return (
												<Box
													sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
												>
													<Chip
														key={value}
														label={
															cdxReplaceMap[value] != undefined
																? cdxReplaceMap[value]
																: value
														}
													/>
												</Box>
											);
										}
									}}
								>
									{Object.entries(displayOrg).map((entry) => {
										return (
											<MenuItem
												key={entry[0]}
												value={
													cdxReplaceMap[entry[0]] != undefined
														? cdxReplaceMap[entry[0]]
														: entry[0]
												}
											>
												{entry[0]}
											</MenuItem>
										);
									})}
								</Select>
							</Box>

							{selectedOrg.length > 0 && selectedGroup != "N/A" ? (
								<Box>
									<Select
										sx={{ mt: "24px" }}
										fullWidth
										value={selectedGroup}
										onChange={(e: SelectChangeEvent<typeof selectedGroup>) => {
											const {
												target: { value },
											} = e;
											setSelectedGroup(value);
											const lookupValue =
												cdxReplaceMap[value] != undefined
													? cdxReplaceMap[value]
													: value;
											const possibleUnits = Object.keys(
												displayOrg[selectedOrg][lookupValue],
											);
											if (
												possibleUnits.length === 1 &&
												possibleUnits[0] === "N/A"
											) {
												setSelectedUnit("N/A");
												const possibleRoles = Object.keys(
													displayOrg[selectedOrg][lookupValue]["N/A"],
												);
												if (possibleRoles.length === 0) {
													setSelectedRole("N/A");
												}
											} else if (possibleUnits.length === 1) {
												setSelectedUnit(possibleUnits[0]);
												const possibleRoles = Object.keys(
													displayOrg[selectedOrg][lookupValue][
														possibleUnits[0]
													],
												);
												if (possibleRoles.length === 0) {
													setSelectedRole("N/A");
												} else {
													setSelectedRole("");
												}
											} else {
												setSelectedUnit("");
												setSelectedRole("");
											}
										}}
										displayEmpty
										renderValue={(value: any) => {
											if (value.length === 0) {
												return (
													<Typography color="gray">Select group</Typography>
												);
											} else {
												return (
													<Box
														sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
													>
														<Chip
															key={value}
															label={
																cdxReplaceMap[value] != undefined
																	? cdxReplaceMap[value]
																	: value
															}
														/>
													</Box>
												);
											}
										}}
									>
										{Object.entries(displayOrg[selectedOrg]).map((entry) => {
											return (
												<MenuItem
													key={entry[0]}
													value={
														cdxReplaceMap[entry[0]] != undefined
															? cdxReplaceMap[entry[0]]
															: entry[0]
													}
												>
													{entry[0]}
												</MenuItem>
											);
										})}
									</Select>
								</Box>
							) : (
								<></>
							)}

							{selectedGroup.length > 0 &&
							Object.keys(displayOrg[selectedOrg]).includes(selectedGroup) &&
							selectedUnit !== "N/A" ? (
								<Box>
									<Select
										sx={{ mt: "24px" }}
										fullWidth
										value={selectedUnit}
										onChange={(e: SelectChangeEvent<typeof selectedUnit>) => {
											const {
												target: { value },
											} = e;
											setSelectedUnit(value);
											const possibleRoles =
												displayOrg[selectedOrg][selectedGroup][value] ===
													undefined && cdxReplaceMap[value] !== undefined
													? displayOrg[selectedOrg][selectedGroup][
															cdxReplaceMap[value]
														]
													: displayOrg[selectedOrg][selectedGroup][value];
											if (
												possibleRoles.length === 0 ||
												(possibleRoles.length === 1 &&
													possibleRoles[0] === "N/A")
											) {
												setSelectedRole("N/A");
											} else if (possibleRoles.length === 1) {
												setSelectedRole(possibleRoles[0]);
											} else {
												setSelectedRole("");
											}
										}}
										displayEmpty
										renderValue={(value: any) => {
											if (value.length === 0) {
												return (
													<Typography color="gray">Select unit</Typography>
												);
											} else {
												return (
													<Box
														sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
													>
														<Chip
															key={value}
															label={
																cdxReplaceMap[value] != undefined
																	? cdxReplaceMap[value]
																	: value
															}
														/>
													</Box>
												);
											}
										}}
									>
										{Object.entries(displayOrg[selectedOrg][selectedGroup]).map(
											(entry) => {
												return (
													<MenuItem
														key={entry[0]}
														value={
															cdxReplaceMap[entry[0]] != undefined
																? cdxReplaceMap[entry[0]]
																: entry[0]
														}
													>
														{entry[0]}
													</MenuItem>
												);
											},
										)}
									</Select>
								</Box>
							) : (
								<></>
							)}

							{selectedUnit.length > 0 && selectedRole != "N/A" ? (
								<Box>
									<Select
										sx={{ mt: "24px" }}
										fullWidth
										value={selectedRole}
										onChange={(e: SelectChangeEvent<typeof selectedRole>) => {
											const {
												target: { value },
											} = e;
											setSelectedRole(value);
										}}
										displayEmpty
										renderValue={(value: any) => {
											if (value.length === 0) {
												return (
													<Typography color="gray">Select role</Typography>
												);
											} else {
												return (
													<Box
														sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
													>
														<Chip
															key={value}
															label={
																cdxReplaceMap[value] != undefined
																	? cdxReplaceMap[value]
																	: value
															}
														/>
													</Box>
												);
											}
										}}
									>
										{displayOrg[selectedOrg][selectedGroup][selectedUnit].map(
											(entry) => {
												return (
													<MenuItem
														key={entry}
														value={
															cdxReplaceMap[entry] != undefined
																? cdxReplaceMap[entry]
																: entry
														}
													>
														{entry}
													</MenuItem>
												);
											},
										)}
									</Select>
								</Box>
							) : (
								<></>
							)}

							<Box alignItems="center" display="flex" gap={4}>
								{/District X/g.test(selectedGroup) ||
								/District X/g.test(selectedRole) ? (
									<Box>
										<Select
											multiple
											sx={{ mt: "24px" }}
											fullWidth
											value={selectedDistricts}
											onChange={(
												e: SelectChangeEvent<typeof selectedDistricts>,
											) => {
												const {
													target: { value },
												} = e;
												setSelectedDistricts(
													typeof value === "string" ? value.split(",") : value,
												);
											}}
											displayEmpty
											renderValue={(value: any) => {
												if (value.length === 0) {
													return (
														<Typography color="gray">
															Select district
														</Typography>
													);
												} else {
													return (
														<Box
															sx={{
																display: "flex",
																flexWrap: "wrap",
																gap: 0.5,
															}}
														>
															{value.map((val: string) => (
																<Chip key={val} label={val} />
															))}
														</Box>
													);
												}
											}}
										>
											{allDistricts.map((entry) => {
												return (
													<MenuItem key={entry} value={entry}>
														{entry}
													</MenuItem>
												);
											})}
										</Select>
									</Box>
								) : (
									<></>
								)}

								{/County X/g.test(selectedRole) ? (
									<Box>
										<Select
											multiple
											sx={{ mt: "24px" }}
											fullWidth
											value={selectedCounties}
											onChange={(
												e: SelectChangeEvent<typeof selectedCounties>,
											) => {
												const {
													target: { value },
												} = e;
												setSelectedCounties(
													typeof value === "string" ? value.split(",") : value,
												);
											}}
											displayEmpty
											renderValue={(value: any) => {
												if (value.length === 0) {
													return (
														<Typography color="gray">Select county</Typography>
													);
												} else {
													return (
														<Box
															sx={{
																display: "flex",
																flexWrap: "wrap",
																gap: 0.5,
															}}
														>
															{value.map((val: string) => (
																<Chip key={val} label={val} />
															))}
														</Box>
													);
												}
											}}
										>
											{selectedDistricts.length > 0
												? allCounties.map((cty) => {
														if (
															selectedDistricts.includes(reverseCountyList[cty])
														)
															return (
																<MenuItem key={cty} value={cty}>
																	{cty}
																</MenuItem>
															);
													})
												: allCounties.map((c) => {
														return (
															<MenuItem key={c} value={c}>
																{c}
															</MenuItem>
														);
													})}
										</Select>
									</Box>
								) : (
									<></>
								)}

								{/County X/g.test(selectedRole) &&
								selectedCounties.some((v) => v === "Cook") ? (
									<Box>
										<Select
											multiple
											sx={{ mt: "24px" }}
											fullWidth
											value={selectedCouncils}
											onChange={(
												e: SelectChangeEvent<typeof selectedCouncils>,
											) => {
												const {
													target: { value },
												} = e;
												setSelectedCouncils(
													typeof value === "string" ? value.split(",") : value,
												);
											}}
											displayEmpty
											renderValue={(value: any) => {
												if (value.length === 0) {
													return (
														<Typography color="gray">Select council</Typography>
													);
												} else {
													return (
														<Box
															sx={{
																display: "flex",
																flexWrap: "wrap",
																gap: 0.5,
															}}
														>
															{value.map((val: string) => (
																<Chip key={val} label={val} />
															))}
														</Box>
													);
												}
											}}
										>
											{council_list.map((c) => {
												return (
													<MenuItem key={c} value={c}>
														{c}
													</MenuItem>
												);
											})}
										</Select>
									</Box>
								) : (
									<></>
								)}

								{/Role X/g.test(selectedRole) ? (
									<Box>
										<Select
											displayEmpty
											sx={{ mt: "24px" }}
											value={selectedDistrictRole}
											onChange={(e) => {
												const { value } = e.target;
												setSelectedDistrictRoles(value);
											}}
											renderValue={(selected) => {
												if (selected.length === 0) {
													return (
														<Typography color="gray">Select Role</Typography>
													);
												}

												return (
													<Box
														sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
													>
														{selectedDistrictRole.length > 0 && (
															<Chip
																key={selectedDistrictRole}
																label={selectedDistrictRole}
															/>
														)}
													</Box>
												);
											}}
										>
											{role_list.map((role) => (
												<MenuItem key={role} value={role}>
													{role}
												</MenuItem>
											))}
										</Select>
									</Box>
								) : null}
							</Box>
							{needsOrganization && (
								<OrganizationPicker
									value={organization}
									onChange={setOrganization}
								/>
							)}
							{cleanGroups.length > 0 && cleanGroups !== "[]" ? (
								<Box>
									<Typography marginTop={4} fontSize={16} fontWeight={600}>
										You will be added to the following groups after approval:
									</Typography>
									{JSON.parse(cleanGroups).map((grp: string, index: number) => {
										return <Chip key={`${index}-${grp}`} label={grp} />;
									})}
								</Box>
							) : (
								<></>
							)}
						</Box>
					</Card>
				</Box>
			</Box>
			<ErrorReportModal
				open={openErrorModal}
				onClose={() => setOpenErrorModal(false)}
				pageType="LOGIN"
			/>
		</>
	);
};
